// new
import React, { useState, useEffect, useRef, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "../../Component/CallTransfer/CallTransfer.module.css";
import HeaderBar from "../HeaderBar/HeaderBar";
import {
  fetchLlmDetails,
  updateLlm,
  getBusinessDetailsByBusinessId,
  getAgentTransferNumbers,
  bulkSaveAgentTransferNumbers,
  deleteAgentTransferNumberById,
  getAgentSmsSetting,
  updateAgentSmsSetting,
  getAgentById,
} from "../../Store/apiStore";
import { getAgentEngineId } from "../../utils/agentEngineId";
import { markSetupStep } from "../../lib/setupScore";
import Loader2 from "../Loader2/Loader2";
import PopUp from "../Popup/Popup";
import AnimatedButton from "../AnimatedButton/AnimatedButton";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import Tooltip from "../TooltipSteps/Tooltip";

const PRESETS = ["Sales", "Billing", "Support", "Accounts", "Service"];

// Everything NOT in this set is silently stripped from the Department
// input as the user types or pastes. Allowed: letters, digits,
// whitespace, &, /, -.
const DEPARTMENT_NAME_DISALLOWED = /[^A-Za-z0-9\s&/\-]/g;

const MIN_DEPARTMENT_NAME_LENGTH = 3;

const dialToCountry = {
  1: "us",
  20: "eg",
  211: "ss",
  212: "ma",
  213: "dz",
  216: "tn",
  218: "ly",
  220: "gm",
  221: "sn",
  222: "mr",
  223: "ml",
  224: "gn",
  225: "ci",
  226: "bf",
  227: "ne",
  228: "tg",
  229: "bj",
  230: "mu",
  231: "lr",
  232: "sl",
  233: "gh",
  234: "ng",
  235: "td",
  236: "cf",
  237: "cm",
  238: "cv",
  239: "st",
  240: "gq",
  241: "ga",
  242: "cg",
  243: "cd",
  244: "ao",
  245: "gw",
  246: "io",
  247: "ac",
  248: "sc",
  249: "sd",
  250: "rw",
  251: "et",
  252: "so",
  253: "dj",
  254: "ke",
  255: "tz",
  256: "ug",
  257: "bi",
  258: "mz",
  260: "zm",
  261: "mg",
  262: "re",
  263: "zw",
  264: "na",
  265: "mw",
  266: "ls",
  267: "bw",
  268: "sz",
  269: "km",
  290: "sh",
  291: "er",
  297: "aw",
  298: "fo",
  299: "gl",
  30: "gr",
  31: "nl",
  32: "be",
  33: "fr",
  34: "es",
  35: "reserved",
  36: "hu",
  37: "reserved",
  39: "it",
  40: "ro",
  41: "ch",
  42: "cz",
  43: "at",
  44: "gb",
  45: "dk",
  46: "se",
  47: "no",
  48: "pl",
  49: "de",
  50: "reserved",
  51: "pe",
  52: "mx",
  53: "cu",
  54: "ar",
  55: "br",
  56: "cl",
  57: "co",
  58: "ve",
  59: "reserved",
  60: "my",
  61: "au",
  62: "id",
  63: "ph",
  64: "nz",
  65: "sg",
  66: "th",
  67: "reserved",
  68: "reserved",
  69: "reserved",
  7: "ru",
  81: "jp",
  82: "kr",
  84: "vn",
  86: "cn",
  90: "tr",
  91: "in",
  92: "pk",
  93: "af",
  94: "lk",
  95: "mm",
  98: "ir",
  350: "gi",
  351: "pt",
  352: "lu",
  353: "ie",
  354: "is",
  355: "al",
  356: "mt",
  357: "cy",
  358: "fi",
  359: "bg",
  370: "lt",
  371: "lv",
  372: "ee",
  373: "md",
  374: "am",
  375: "by",
  376: "ad",
  377: "mc",
  378: "sm",
  379: "va",
  380: "ua",
  381: "rs",
  382: "me",
  383: "xk",
  385: "hr",
  386: "si",
  387: "ba",
  389: "mk",
  420: "cz",
  421: "sk",
  423: "li",
  500: "fk",
  501: "bz",
  502: "gt",
  503: "sv",
  504: "hn",
  505: "ni",
  506: "cr",
  507: "pa",
  508: "pm",
  509: "ht",
  590: "gp",
  591: "bo",
  592: "gy",
  593: "ec",
  594: "gf",
  595: "py",
  596: "mq",
  597: "sr",
  598: "uy",
  599: "cw",
  670: "tl",
  672: "aq",
  673: "bn",
  674: "nr",
  675: "pg",
  676: "to",
  677: "sb",
  678: "vu",
  679: "fj",
  680: "pw",
  681: "wf",
  682: "ck",
  683: "nu",
  685: "ws",
  686: "ki",
  687: "nc",
  688: "tv",
  689: "pf",
  690: "tk",
  691: "fm",
  692: "mh",
  850: "kp",
  852: "hk",
  853: "mo",
  855: "kh",
  856: "la",
  880: "bd",
  886: "tw",
  960: "mv",
  961: "lb",
  962: "jo",
  963: "sy",
  964: "iq",
  965: "kw",
  966: "sa",
  967: "ye",
  968: "om",
  971: "ae",
  972: "il",
  973: "bh",
  974: "qa",
  975: "bt",
  976: "mn",
  977: "np",
  992: "tj",
  993: "tm",
  994: "az",
  995: "ge",
  996: "kg",
  998: "uz",
};

const dialCodeFromIso2 = (iso2) => {
  if (!iso2) return undefined;
  const entry = Object.entries(dialToCountry).find(
    ([, cc]) => cc === iso2.toLowerCase()
  );
  return entry ? String(entry[0]) : undefined;
};

// Go-live wizard ka phone field: dial code input ke bahar (+1 | number).
// Row me `phone` poora number (dial + national) hi rehta hai — yahan sirf
// display ke liye todte hain.
const splitWizardPhone = (item) => {
  const dial =
    String(item?.dialCode || "").replace(/\D/g, "") ||
    dialCodeFromIso2(item?.countryCode || "us") ||
    "1";
  const full = String(item?.phone || "").replace(/\D/g, "");
  const national = full.startsWith(dial) ? full.slice(dial.length) : full;
  return { dial, national };
};

const countryNameToIso2 = {
  India: "in",
  Australia: "au",
  "United States": "us",
  "United Kingdom": "gb",
  Canada: "ca",
  Singapore: "sg",
  "New Zealand": "nz",
  Germany: "de",
  France: "fr",
  Italy: "it",
  Spain: "es",
};

const normalizePhoneIntoTransfer = (condition, raw) => {
  if (!raw) return null;
  const clean = String(raw).replace(/[^\d+]/g, "");
  const input = clean.startsWith("+") ? clean : `+${clean}`;
  const parsed = parsePhoneNumberFromString(input);
  if (!parsed || !parsed.isValid()) return null;
  const dial = parsed.countryCallingCode;
  const iso2 =
    parsed.country?.toLowerCase() || (dial && dialToCountry[dial]) || "us";
  const digits = parsed.number.replace("+", "");
  return {
    id: null,
    condition,
    phone: digits,
    dialCode: dial,
    countryCode: iso2,
  };
};

// --- helpers for variable key & labels ---
const normalizeCondition = (s = "") =>
  s.trim().replace(/\s+/g, " ").replace(/_/g, " ");

const keyFromCondition = (s = "") => {
  const safe = normalizeCondition(s)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

  return `${safe || "department"}_number`;
};

// Returns "" when the department name is valid, otherwise a human-readable
// message. Trimmed via normalizeCondition so leading/trailing spaces don't
// count toward the min/max limits.
const getDepartmentNameError = (value = "") => {
  const name = normalizeCondition(value);

  if (!name) {
    return "Department name is required.";
  }

  if (name.length < MIN_DEPARTMENT_NAME_LENGTH) {
    return `Department name must be at least ${MIN_DEPARTMENT_NAME_LENGTH} characters.`;
  }

  return "";
};

const titleCase = (s = "") =>
  normalizeCondition(s)
    .toLowerCase()
    .replace(/(^|\s)\S/g, (c) => c.toUpperCase());

const inferModeFromCondition = (condition) => {
  const c = (condition || "").trim().toLowerCase();
  if (PRESETS.map((p) => p.toLowerCase()).includes(c)) return "preset";
  if (!c) return "preset";
  return "custom";
};

const dbRowToTransfer = (r) => ({
  id: r?.id ?? null,
  condition: normalizeCondition(r?.condition || ""),
  phone: String(r?.phone || "").replace(/\D/g, ""),
  email: (r?.email != null && r.email !== "null") ? String(r.email).trim().toLowerCase() : "",
  dialCode: String(r?.dialCode || "").replace(/\D/g, ""),
  countryCode: String(r?.countryCode || "us").toLowerCase(),
  send_sms: Boolean(r?.send_sms ?? r?.sendSms ?? r?.sendSMS ?? false),
  is_fallback: Boolean(r?.is_fallback ?? r?.isFallback ?? false),
  // Initial fingerprint based on DB-loaded values. Won't change again
  // unless the sync effect (biz-phone change) bumps it.
  _syncFingerprint: `db:${String(r?.countryCode || "us").toLowerCase()}:${String(r?.phone || "").replace(/\D/g, "")}`,
});

const mergeTransfersPreferDB = (llmList = [], dbList = []) => {
  const map = new Map();

  llmList.forEach((t) => {
    const key = normalizeCondition(t.condition).toLowerCase();
    map.set(key, { ...t, id: t.id ?? null, email: t.email || "", send_sms: Boolean(t.send_sms), is_fallback: Boolean(t.is_fallback) });
  });

  dbList.forEach((t) => {
    const key = normalizeCondition(t.condition).toLowerCase();
    const existing = map.get(key);
    // DB wins
    map.set(key, {
      ...existing,
      ...t,
      id: t.id ?? existing?.id ?? null,
      email: t.email || "",
      send_sms: Boolean(t.send_sms),
      is_fallback: Boolean(t.is_fallback),
    });
  });

  return Array.from(map.values());
};

const toDbNumbersPayload = (list = []) => {
  return (list || [])
    .map((t) => ({
      id: t.id ?? undefined,
      condition: normalizeCondition(t.condition || ""),
      phone: String(t.phone || "").replace(/\D/g, ""),
      email: t.email ? String(t.email).trim().toLowerCase() : null,
      dialCode: String(t.dialCode || "").replace(/\D/g, ""),
      countryCode: String(t.countryCode || "us").toLowerCase(),
      send_sms: Boolean(t.send_sms),
      is_fallback: Boolean(t.is_fallback),
    }))
    .filter((x) => x.condition && x.phone && x.dialCode);
};


// Stable string fingerprint of the user-editable data. Used only to answer
// "did anything change vs. what's saved?". Blank rows are ignored and row
// order doesn't matter.
const buildSnapshot = (list = []) => {
  const rows = (list || [])
    .map((t) => ({
      condition: normalizeCondition(
        t._mode === "custom" ? t._customName || "" : t.condition || ""
      ),
      phone: String(t.phone || "").replace(/\D/g, ""),
      dialCode: String(t.dialCode || "").replace(/\D/g, ""),
      countryCode: String(t.countryCode || "us").toLowerCase(),
      email:
        t.email && t.email !== "null"
          ? String(t.email).trim().toLowerCase()
          : "",
      is_fallback: Boolean(t.is_fallback),
    }))
    .filter((r) => r.condition || r.phone || r.email);

  rows.sort((a, b) =>
    (a.condition + a.phone).localeCompare(b.condition + b.phone)
  );
  return JSON.stringify(rows);
};

const getSelectedDepartments = (transfers) =>
  transfers
    .map((t) => normalizeCondition(t.condition))
    .filter(Boolean)
    .map((c) => c.toLowerCase());

// Thin capsule toggle — 36×18px, purple ON / gray OFF. Lives inline so
// no extra component file. Knob is 14×14 with 2px inset on either side
// (right-edge offset = 36 − 14 − 2 = 20).
const Toggle = ({ checked, onChange, disabled }) => {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      aria-pressed={checked}
      className={`${styles.toggle}${checked ? ` ${styles.toggleOn}` : ""}`}
    >
      <span
        className={`${styles.toggleKnob}${checked ? ` ${styles.toggleKnobOn}` : ""}`}
      />
    </button>
  );
};

function CallTransfer({
  // Wizard integration — when true, the component hides its HeaderBar
  // and signals successful save through the provided callbacks instead
  // of just toasting a popup. Default behavior is untouched.
  wizardMode = false,
  onWizardNext,
  onWizardBack,
  // Optional: pass business details directly to stay in sync with parent
  businessDetails: propBusinessDetails = null,
  // Optional copy override for the title/subtitle. GoLiveWizard's
  // CallRoutingStep wrapper passes Figma 13 strings; PostPurchaseSetup
  // omits and gets the existing component defaults. Missing keys fall
  // through to defaults so partial overrides are safe.
  //
  //   { lightTitle, boldTitle, subtitle }
  //
  // NOTE on default triage rule: a single "Service" row pre-filled with
  // the business phone is already auto-created by the existing
  // empty-state useEffect (see createEmptyRow + the bizLoaded/llmLoaded
  // effect below). Both PostPurchase and GoLive share that behaviour, so
  // no separate seed flag is needed — spec REXPT.pdf §2 Phase 4 Screen
  // 13 Step 3 default rule requirement is already satisfied.
  headings = null,
  /*
   * GoLiveWizard-only: extra rows to seed once if the user has zero
   * existing triage rules. Each row carries a single `condition` string
   * shaped like `Label — Description` (see triageDefaultsByCategory.js).
   * The label is the department, the description is the situational
   * trigger; both flow through buildTransferPrompt unchanged so the LLM
   * gets full context for transfer decisions without needing a separate
   * notes column on AgentTransferNumber.
   *
   * Shape: [{ condition: string }, ...]
   *
   * Pre-fills `phone` from the business phone (per user spec) and
   * appends AFTER the auto-created "Service" row. PostPurchaseSetup
   * omits this prop and gets the single-row behaviour. Seeded once per
   * agent (localStorage marker `gl_triage_seeded_<agentId>`) — even if
   * the user deletes all rows we don't re-seed.
   */
  seedCategoryRows = null,
  customHeader = null,
} = {}) {
  // Pounce settings se aaye to save ke baad wahin wapas
  const returnTo = useLocation().state?.returnTo;
  const navigate = useNavigate();
  // Sirf asli save ke baad back; delete/double-close pe nahi
  const backOnCloseRef = useRef(false);
  const agentLoadFailedRef = useRef(false);
  const [agentId, setAgentId] = useState("");
  const [llmId, setLlmId] = useState("");
  const [transfers, setTransfers] = useState([]);

  const [businessDetails, setBusinessDetails] = useState(propBusinessDetails);
  const [bizLoaded, setBizLoaded] = useState(!!propBusinessDetails);
  const [llmLoaded, setLlmLoaded] = useState(false);
  const [loading, setLoading] = useState(false);

  // What is currently persisted in the DB. Submit is only allowed when the
  // on-screen rows differ from this.
  const [savedRows, setSavedRows] = useState([]);

  const isDirty = useMemo(
    () => buildSnapshot(transfers) !== buildSnapshot(savedRows),
    [transfers, savedRows]
  );

  // The wizard's submit button sits in a position:fixed shelf, so it does not
  // push page content up — the last department card scrolled underneath it and
  // its Email field could not be reached. A spacer below the cards reserves the
  // room instead.
  //
  // Measured rather than hardcoded: the shelf's height depends on the button,
  // which wraps to two lines on a narrow screen, and on --wizard-cta-lift when
  // a host wizard raises the shelf above its own footer. A fixed number would
  // be right until one of those changed.
  const wizardShelfRef = useRef(null);
  const [shelfHeight, setShelfHeight] = useState(0);

  // Keep state in sync with prop if passed from parent wizard
  useEffect(() => {
    if (propBusinessDetails) {
      setBusinessDetails(propBusinessDetails);
      setBizLoaded(true);
    }
  }, [propBusinessDetails]);

  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    const el = wizardShelfRef.current;
    if (!el) {
      setShelfHeight(0);
      return;
    }
    const measure = () => setShelfHeight(el.offsetHeight || 0);
    measure();
    // Re-measure on resize: the button reflows on narrow screens, and the
    // spacer has to follow or the gap is wrong again.
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [wizardMode, loading]);
  const [popupType, setPopupType] = useState(null);
  const [popupMessage, setPopupMessage] = useState("");
  const [prevDynanamicVar, setPrevDynamicVar] = useState({});
  const [removingIndex, setRemovingIndex] = useState(null);
  const [sendSmsEnabled, setSendSmsEnabled] = useState(false);
  const [savingSmsToggle, setSavingSmsToggle] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [activeToggleIndex, setActiveToggleIndex] = useState(null);

  // Department field errors only appear once the user has clicked Submit.
  // Before that the field stays quiet while typing, matching the ticket's
  // "show validation on submit" requirement.
  const [showDepartmentErrors, setShowDepartmentErrors] = useState(false);

  // StrictMode-safe load guard. React 19 StrictMode double-fires every
  // effect on mount in development; without this ref, the LLM load
  // ran twice and its second `setTransfers(merged)` reverted the rows
  // we had already seeded (only the Service row would remain visible).
  // Refs survive the double-fire because state isn't reset between them.
  const llmLoadStartedRef = useRef(false);

  const getBizDefaults = () => {
    if (!businessDetails) return { iso2: "us", dial: "1", rawPhone: "" };
    // knowledge_base_texts is stored as JSON string in DB — parse it first
    let kbt = {};
    try {
      kbt = typeof businessDetails.knowledge_base_texts === "string"
        ? JSON.parse(businessDetails.knowledge_base_texts)
        : businessDetails.knowledge_base_texts || {};
    } catch { kbt = {}; }
    const raw = kbt?.phone || businessDetails?.businessPhone || "";
    const parsed = raw ? parsePhoneNumberFromString(raw) : null;
    let iso2 = parsed?.country ? parsed.country.toLowerCase() : undefined;
    let dial = parsed?.countryCallingCode || undefined;

    if (!iso2) {
      const cc = (businessDetails?.country_code || "").trim();
      if (cc.length === 2) iso2 = cc.toLowerCase();
    }
    if (!dial && iso2) dial = dialCodeFromIso2(iso2);

    if (!iso2) {
      const byName =
        businessDetails?.country && countryNameToIso2[businessDetails.country];
      if (byName) {
        iso2 = byName;
        if (!dial) dial = dialCodeFromIso2(byName);
      }
    }

    const finalDial = (dial || "1").replace(/\D/g, "");
    let phoneValue = "";
    if (parsed) {
      phoneValue = `${parsed.countryCallingCode}${parsed.nationalNumber}`;
    } else {
      const cleanRaw = raw.replace(/\D/g, "");
      phoneValue = cleanRaw.startsWith(finalDial) ? cleanRaw : `${finalDial}${cleanRaw}`;
    }

    return { iso2: iso2 || "us", dial: finalDial, phoneValue };
  };

  // First-row default — the auto-created Service row pre-filled with the
  // business phone. Used only for the initial empty-state seed (when
  // transfers.length === 0). User-clicked "Add" rows use createBlankRow
  // below so they don't duplicate the Service department or copy biz
  // phone (which caused both dropdowns to render empty + share the same
  // number when the dropdown filter excluded the duplicate).
  const createEmptyRow = () => {
    const { iso2, dial, phoneValue } = getBizDefaults();
    return {
      id: null,
      condition: "Service",
      phone: phoneValue,
      email: "",
      dialCode: dial,
      countryCode: iso2,
      _mode: "preset",
      _customName: "",
      // _syncFingerprint — bumped only by the programmatic sync effect
      // (biz-phone change). The PhoneInput is keyed on this so the flag
      // remounts when sync swaps the country, but stays mounted during
      // user typing / flag-dropdown changes — those preserve focus.
      _syncFingerprint: `${iso2}:${phoneValue}`,
      send_sms: Boolean(sendSmsEnabled),
      // Default-on fallback for the first auto-created Service row.
      // Ensures every new agent has at least one fallback destination so
      // the agent never hits a dead end on an unhandled transfer intent.
      is_fallback: true,
    };
  };

  // Blank row added by the user via the + button. Empty department +
  // empty phone — they pick a department and type a custom number for
  // that team. Inheriting biz country/dial code is fine since most
  // departments share the same country.
  const createBlankRow = () => {
    const { iso2, dial } = getBizDefaults();
    return {
      id: null,
      condition: "",
      phone: "",
      email: "",
      dialCode: dial,
      countryCode: iso2,
      _mode: "preset",
      _customName: "",
      _syncFingerprint: `init:${Date.now()}`,
      send_sms: Boolean(sendSmsEnabled),
      is_fallback: false,
    };
  };

  const updateTransfer = (index, patch) => {
    setTransfers((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  };

  const prepareTransfersWithDialCode = (list) =>
    list.map((t) => ({
      ...t,
      condition: normalizeCondition(t.condition || ""),
      phone: (t.phone || "").trim(),
      email: (t.email && t.email !== "null") ? t.email.trim().toLowerCase() : "",
      dialCode: String(t.dialCode || "").trim(),
      countryCode: String(t.countryCode || "us").toLowerCase(),
      send_sms: Boolean(t.send_sms),
      is_fallback: Boolean(t.is_fallback),
    }));

  const syncSessionCache = (list) => {
    try {
      const clean = (list || []).map(({ _mode, _customName, ...rest }) => rest);
      sessionStorage.setItem("agentGeneralTools", JSON.stringify(clean));
    } catch { }
  };

  const buildTransferPrompt = (formattedTransfers) => {
    // condition is shaped as either a plain label ("Sales") or
    // "Label — Trigger description" (e.g. "Vendor / Supplier — deliveries,
    // invoices, accounts"). The trigger is what the caller will actually
    // describe in their own words; the label is just an internal name for
    // the department. Splitting here so the LLM matches on INTENT rather
    // than expecting the caller to recite the department name.
    const splitLabel = (cond) => {
      const m = String(cond || "").trim().match(/^(.+?)\s+[—–-]\s+(.+)$/);
      return m
        ? { label: m[1].trim(), trigger: m[2].trim() }
        : { label: String(cond || "").trim(), trigger: "" };
    };

    const rows = formattedTransfers.map((t) => {
      const { label, trigger } = splitLabel(t.condition);
      return {
        label: titleCase(label),
        trigger,
        key: keyFromCondition(t.condition),
        isFallback: Boolean(t.is_fallback),
      };
    });

    const fallback = rows.find((r) => r.isFallback);

    const departmentsList = rows
      .map((r) =>
        r.trigger
          ? `- **${r.label}** → {{${r.key}}} — use when the caller mentions: ${r.trigger}`
          : `- **${r.label}** → {{${r.key}}}`,
      )
      .join("\n");

    const triggerLines = rows
      .map((r) => {
        const intentClause = r.trigger
          ? `the caller mentions or asks about ${r.trigger}`
          : `the caller asks for ${r.label}`;
        return `- If ${intentClause} → transfer to {{${r.key}}} (${r.label}).`;
      })
      .join("\n");

    const labelList = rows.map((r) => r.label).join(", ");

    const fallbackPrompt = fallback
      ? `\n\n## FALLBACK AUTO-TRANSFER RULE:
If the caller becomes **irritated, angry, frustrated, or upset**, OR the agent is **unable to resolve the caller's issue** or **cannot understand what the caller needs**, automatically transfer the call to the fallback number: **${fallback.label}** at {{${fallback.key}}}.

**Triggers for fallback transfer:**
- Caller uses angry/frustrated language (e.g., "This is ridiculous", "I want to speak to a real person", "You're not helping")
- Caller repeatedly asks for something the agent cannot handle
- Caller explicitly asks to speak to someone else or a human
- Agent cannot determine the caller's intent after 2-3 attempts
- Caller expresses dissatisfaction with the agent's responses

**IMPORTANT:** When triggering a fallback transfer, say: "I understand your concern. Let me connect you with someone who can help you better." Then immediately transfer to {{${fallback.key}}}.`
      : "";

    return `The caller may need to be transferred. Match the caller's **intent** — what they are calling about, in their own words — to one of the destinations below. Callers will rarely (if ever) name the department; instead they will describe their situation, so use the trigger description after each destination to decide.

## AVAILABLE DEPARTMENTS FOR TRANSFER:
${departmentsList}

## CRITICAL RULES:
1. **Only transfer to destinations listed above.** Never invent destinations or use one department's number for another department's purpose.

2. **Match by INTENT, not by literal keyword.** Read the trigger description after each destination ("use when the caller mentions: …"). That is what the caller will actually describe. Do NOT require the caller to literally say the department name.

3. **If the caller's intent doesn't match any destination's trigger**, do NOT transfer. Say: "I apologize, I don't have a direct line for that. I can connect you with: ${labelList}. Would any of those help, or would you prefer I take a message and have someone call you back?"

4. **For URGENT or emergency situations** (burst pipes, gas leaks, flooding, no power, accidents), prioritize the destination whose trigger description mentions emergency/urgent/critical — even if the caller doesn't use the word "emergency". If no such destination exists, do NOT transfer to a different department; take the caller's details and mark the request URGENT.

5. **Unknown person requests**: If the caller asks for a specific person or team not listed, say: "I don't have a direct line for that. Can I help you with something else, or would you like me to take a message?"

6. **If the transfer fails and the caller asks to try again, attempt the transfer once more.**

## TRANSFER TRIGGERS:
${triggerLines}
${fallbackPrompt}

**IMPORTANT: Match the caller's intent to the trigger description, never the literal department name. Never substitute one department for another.**`;
  };

  const persistTransfers = async (
    llmIdParam,
    transfersList,
    cleanedPrevVars
  ) => {
    const token = localStorage.getItem("token") || "";

    const formatted = prepareTransfersWithDialCode(
      transfersList.map(({ _mode, _customName, ...rest }) => rest)
    );

    // Python agents have no Retell LLM to rewrite — the DB rows the caller
    // already updated are the ddddd story, so there is nothing left to do.
    if (!llmIdParam) {
      syncSessionCache(formatted);
      return;
    }

    const llmRes = await fetchLlmDetails(llmIdParam);
    const currentConfig = llmRes?.data?.data || llmRes?.data?.data?.data;

    if (!currentConfig) throw new Error("Could not load current LLM config.");

    // Rebuild the transfer tool prompt with remaining numbers
    const existingTools = currentConfig.general_tools || [];
    const validTransfers = formatted.filter((t) => t.condition && t.phone && t.dialCode);

    if (validTransfers.length > 0) {
      const fullPrompt = buildTransferPrompt(validTransfers);
      const timestamp = Date.now();
      const transferTool = {
        type: "transfer_call",
        name: `transfer_on_inferred_${timestamp}`,
        transfer_destination: { type: "inferred", prompt: fullPrompt },
        transfer_option: {
          type: "warm_transfer",
          enable_human_detection: true,
          auto_greet: true,
          voicemail_detection_timeout_ms: 60000,
          transfer_ring_duration_ms: 60000,
          enable_bridge_audio_cue: true,
        },
        custom_sip_headers: {
          "X-Telnyx-Username": `${process.env.REACT_APP_TELNYX_USERNAME}`
        },
        speak_during_execution: true,
        speak_after_execution: true,
        failure_message:
          "I apologize, but the line is busy right now. Please try again later or I can assist you with something else.",
      };

      const toolsWithoutOldTransfer = existingTools.filter(
        (t) =>
          !(
            t?.type === "transfer_call" &&
            String(t?.name || "").startsWith("transfer_on_inferred_")
          )
      );

      const payload = {
        ...currentConfig,
        general_tools: [...toolsWithoutOldTransfer, transferTool],
        default_dynamic_variables: cleanedPrevVars,
      };
      await updateLlm(token, llmIdParam, payload);
    } else {
      // No valid transfers left — remove transfer tool entirely
      const toolsWithoutOldTransfer = existingTools.filter(
        (t) =>
          !(
            t?.type === "transfer_call" &&
            String(t?.name || "").startsWith("transfer_on_inferred_")
          )
      );

      const payload = {
        ...currentConfig,
        general_tools: toolsWithoutOldTransfer,
        default_dynamic_variables: cleanedPrevVars,
      };
      await updateLlm(token, llmIdParam, payload);
    }

    syncSessionCache(formatted);
  };

  // ✅ Business fetch
  useEffect(() => {
    const bizId = sessionStorage.getItem("SelectAgentBusinessId");
    if (!bizId) {
      setShowPopup(true);
      setPopupType("failed");
      setPopupMessage(
        "No business selected. Missing 'SelectAgentBusinessId' in session."
      );
      setBizLoaded(true);
      return;
    }

    (async () => {
      try {
        const data = await getBusinessDetailsByBusinessId(bizId);
        const biz = data?.data ?? data ?? null;
        setBusinessDetails(biz);
      } catch (error) {
        console.error(
          "Error fetching business details:",
          error?.response?.data || error?.message || error
        );
        setShowPopup(true);
        setPopupType("failed");
        setPopupMessage(
          "Failed to fetch business details. " +
          (error?.response?.data || error?.message || "")
        );
      } finally {
        setBizLoaded(true);
      }
    })();
  }, []);

  // ✅ Load LLM vars + DB numbers => merge (DB preferred)
  useEffect(() => {
    // StrictMode guard — see ref decl. Without this, the second mount
    // pass re-runs the load AFTER our seed effect appended category
    // rows, and its setTransfers(merged) wipes them out.
    if (llmLoadStartedRef.current) return;
    llmLoadStartedRef.current = true;

    const agentData = JSON.parse(sessionStorage.getItem("agentDetails"));
    let aId = agentData?.agent?.agent_id || agentData?.agent?.agentId || "";
    // Conversation flow agent ka llmId nahi hota — tab agent_id (getAgentEngineId).
    let lId = getAgentEngineId(agentData?.agent);
    const selectedId = sessionStorage.getItem("SelectAgentId") || "";

    setAgentId(aId);
    setLlmId(lId);

    // A Python-created agent has no Retell LLM, so `llmId` is empty. That used
    // to return early — which also skipped the DB load below, leaving the
    // screen blank even though the numbers were saved. Only the Retell half is
    // conditional now; AgentTransferNumbers is the source of truth for both
    // agent types.
    (async () => {
      try {
        // Purana agentDetails kisi aur agent ka ho to selected agent fresh lao
        if (selectedId && selectedId !== aId) {
          const res = await getAgentById(selectedId).catch(() => null);
          const fresh = res?.data;
          // Fetch fail → purane agent pe save na ho, yahin ruko
          if (!fresh?.agent_id) {
            agentLoadFailedRef.current = true;
            setShowPopup(true);
            setPopupType("failed");
            setPopupMessage("Could not load this agent. Please go back and try again.");
            return;
          }
          aId = fresh.agent_id;
          lId = getAgentEngineId(fresh);
          sessionStorage.setItem("agentDetails", JSON.stringify({ agent: fresh }));
        }
        setAgentId(aId);
        setLlmId(lId);

        // 1) LLM load (Retell agents only)
        const llmIncoming = [];
        if (lId) {
          const llmRes = await fetchLlmDetails(lId);
          const vars = llmRes?.data?.data?.default_dynamic_variables || {};
          setPrevDynamicVar(vars);

          Object.entries(vars).forEach(([k, v]) => {
            if (!k.endsWith("_number")) return;
            const rawCond = k.replace(/_number$/, "");
            const condition = normalizeCondition(rawCond.replace(/_/g, " "));
            const t = normalizePhoneIntoTransfer(condition, v);
            if (t) llmIncoming.push({ ...t, send_sms: false });
          });
        }

        // 2) DB load
        let dbIncoming = [];
        if (aId) {
          const dbRes = await getAgentTransferNumbers(aId);
          const rows =
            dbRes?.data?.data || dbRes?.data || dbRes?.rows || dbRes || [];
          dbIncoming = Array.isArray(rows) ? rows.map(dbRowToTransfer) : [];
        }
        setSavedRows(dbIncoming);

        // 3) Merge
        const merged = mergeTransfersPreferDB(
          dbIncoming.length > 0 ? [] : llmIncoming,
          dbIncoming
        ).map(
          (r) => ({
            ...r,
            _mode: inferModeFromCondition(r.condition),
            _customName:
              inferModeFromCondition(r.condition) === "custom"
                ? r.condition
                : "",
            // if DB didn't have send_sms, fallback to global
            send_sms:
              typeof r.send_sms === "boolean"
                ? r.send_sms
                : Boolean(sendSmsEnabled),
          })
        );

        if (merged.length > 0) {
          setTransfers(merged);
          const fallbackIdx = merged.findIndex((t) => t.is_fallback);
          if (fallbackIdx !== -1) setActiveToggleIndex(fallbackIdx);
        }
      } catch (e) {
        console.error("[CallTransfer] Failed to load LLM/DB transfer numbers", e);
      } finally {
        setLlmLoaded(true);
      }
    })();
  }, []);

  // ✅ After BOTH loads: seed the initial rows.
  //    - If the LLM/DB returned ZERO transfers → seed Service + (optionally)
  //      the category rows.
  //    - If the LLM/DB returned exactly ONE row that is the default Service
  //      row AND `seedCategoryRows` was passed (GoLive triage step), APPEND
  //      the category rows alongside the existing Service row. This handles
  //      the common case where a previous session/agent-creation flow had
  //      already auto-created the Service entry, which would otherwise
  //      cause the empty-state guard to skip seeding entirely (user reports:
  //      "only Service is showing, nothing else").
  //    - Once-only via sessionStorage marker keyed by agentId: if the user
  //      deletes the seeded rows and revisits the step, we do NOT re-seed.
  useEffect(() => {
    if (!bizLoaded || !llmLoaded) return;

    const hasSeedRows =
      Array.isArray(seedCategoryRows) && seedCategoryRows.length > 0;
    // Marker is per-agent so different agents in the same browser session
    // each get their seed exactly once.
    const seedMarkerKey = agentId ? `gl_triage_seeded_${agentId}` : null;
    const alreadySeeded =
      seedMarkerKey && localStorage.getItem(seedMarkerKey) === "1";

    const isOnlyDefaultService =
      transfers.length === 1 &&
      String(transfers[0]?.condition || "").toLowerCase().trim() === "service";

    /*
     * Bug fix — once the per-agent seed marker is set, NEITHER the
     * empty-list path NOR the service-only path may re-seed category rows.
     *
     * However, if the list is COMPLETELY empty (transfers.length === 0),
     * we must show at least one blank card so the user has an entry point.
     * If they have a biz phone and haven't seeded yet, they get the full
     * Service + Categories. If the biz phone is missing OR they've already
     * seeded and deleted everything, they get exactly one blank row.
     */
    const shouldSeedFromEmpty = transfers.length === 0;
    const shouldAppendToService =
      isOnlyDefaultService && hasSeedRows && !alreadySeeded;

    if (!shouldSeedFromEmpty && !shouldAppendToService) return;

    const { iso2, dial, phoneValue } = getBizDefaults();

    if (shouldSeedFromEmpty) {
      if (phoneValue && !alreadySeeded) {
        // Full seed — Service pre-filled with biz phone + optional categories
        const initialRow = createEmptyRow();
        const seededExtras = hasSeedRows
          ? seedCategoryRows.map((r) => ({
            id: null,
            condition: r.condition || "",
            phone: phoneValue,
            email: "",
            dialCode: dial,
            countryCode: iso2,
            _mode: "custom",
            _customName: r.condition || "",
            _syncFingerprint: `${iso2}:${phoneValue}`,
            send_sms: Boolean(sendSmsEnabled),
            is_fallback: false,
          }))
          : [];

        setTransfers([initialRow, ...seededExtras]);
        setActiveToggleIndex(0);

        if (hasSeedRows && seedMarkerKey) {
          localStorage.setItem(seedMarkerKey, "1");
        }
      } else {
        // Fallback — just one blank row so the screen isn't empty
        setTransfers([createBlankRow()]);
      }
    } else if (shouldAppendToService) {
      // Logic for appending extras to an existing Service row (GoLive Triage)
      const seededExtras = seedCategoryRows.map((r) => ({
        id: null,
        condition: r.condition || "",
        phone: phoneValue,
        email: "",
        dialCode: dial,
        countryCode: iso2,
        _mode: "custom",
        _customName: r.condition || "",
        _syncFingerprint: `${iso2}:${phoneValue}`,
        send_sms: Boolean(sendSmsEnabled),
        is_fallback: false,
      }));

      setTransfers((prev) => [...prev, ...seededExtras]);

      if (seedMarkerKey) {
        localStorage.setItem(seedMarkerKey, "1");
      }
    }

    if (hasSeedRows && seedMarkerKey) {
      localStorage.setItem(seedMarkerKey, "1");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bizLoaded, llmLoaded, businessDetails, agentId, transfers.length]);

  useEffect(() => {
    if (!agentId) return;

    (async () => {
      try {
        const res = await getAgentSmsSetting(agentId);
        const enabled = Boolean(
          res?.data?.send_transfer_sms ??
          res?.send_transfer_sms ??
          res?.data?.data?.send_transfer_sms ??
          false
        );

        setSendSmsEnabled(enabled);
        setTransfers((prev) =>
          prev.map((t) => ({
            ...t,
            send_sms: typeof t.send_sms === "boolean" ? t.send_sms : enabled,
          }))
        );
      } catch (e) {
        console.error("Failed to load agent sms setting:", e);
      }
    })();
  }, [agentId]);

  const handleAdd = () => {
    const hasBlank = transfers.some(
      (t) =>
        !(t.condition || "").trim() &&
        !(t.phone || "").trim()
    );

    if (hasBlank) {
      setShowPopup(true);
      setPopupType("failed");
      setPopupMessage("Finish the current empty row before adding another.");
      return;
    }
    // Shift fallback index since new row is prepended at 0
    if (activeToggleIndex !== null) {
      setActiveToggleIndex(activeToggleIndex + 1);
    }
    // User-added rows start blank — they pick the department + type a
    // number specific to that team. (createEmptyRow is reserved for the
    // initial Service+biz-phone seed.)
    setTransfers((prev) => [createBlankRow(), ...prev]);
  };

  // ✅ Remove: LLM + DB
  const handleRemove = async (index) => {
    const removed = transfers[index];
    const remaining = transfers.filter((_, i) => i !== index);
    const insertedEmpty = remaining.length === 0;

    // If the deleted number was the fallback, reset fallback
    if (activeToggleIndex === index) {
      setActiveToggleIndex(null);
      // Clear is_fallback from all remaining
      remaining.forEach((t) => (t.is_fallback = false));
    }

    // Adjust index since an item before active was removed
    if (activeToggleIndex !== null && activeToggleIndex > index) {
      setActiveToggleIndex(activeToggleIndex - 1);
    }

    setRemovingIndex(index);
    setDeleting(true); // 🔥 START loader
    // Allow the user to completely empty their transfer list if they want
    setTransfers(remaining);

    const hadData =
      (removed?.condition && removed.condition.trim()) ||
      (removed?.phone && removed.phone.trim());

    if (!hadData) {
      setRemovingIndex(null);
      setDeleting(false); // 🔥 STOP loader
      return;
    }

    try {
      // 1) LLM vars remove
      const cleanedPrev = { ...prevDynanamicVar };
      const varKey =
        removed?.condition && keyFromCondition(removed.condition || "");
      if (varKey && cleanedPrev[varKey]) delete cleanedPrev[varKey];

      await persistTransfers(llmId, remaining, cleanedPrev);
      setPrevDynamicVar(cleanedPrev);

      // 2) DB delete
      if (removed?.id) {
        await deleteAgentTransferNumberById(removed.id);
      } else if (agentId) {
        await bulkSaveAgentTransferNumbers(
          agentId,
          toDbNumbersPayload(remaining)
        );
      }

      setSavedRows((prev) =>
        removed?.id
          ? prev.filter((r) => r.id !== removed.id)
          : toDbNumbersPayload(remaining)
      );

      setShowPopup(true);
      setPopupType("success");
      setPopupMessage("Number removed.");
    } catch (err) {
      // rollback UI
      setTransfers((prev) => {
        if (insertedEmpty) return [removed];
        const back = [...prev];
        back.splice(index, 0, removed);
        return back;
      });

      setShowPopup(true);
      setPopupType("failed");
      setPopupMessage(
        "Failed to remove number: " +
        (err?.response?.data || err?.message || err)
      );
    } finally {
      setRemovingIndex(null);
      setDeleting(false); // 🔥 STOP loader
    }
  };

  const handleSubmit = async () => {
    if (agentLoadFailedRef.current) {
      setShowPopup(true);
      setPopupType("failed");
      setPopupMessage("Could not load this agent. Please go back and try again.");
      return;
    }

    if (!isDirty) {
      if (!wizardMode) return; // standalone: nothing changed → no API calls

      // Wizard: button doubles as "Continue". Nothing changed and rules already
      // exist in the DB, so just advance, with no API calls.
      if (savedRows.length > 0 && typeof onWizardNext === "function") {
        onWizardNext(savedRows.length);
        return;
      }
      // no saved rows → fall through to the normal validation/popup flow
    }

    try {
      const token = localStorage.getItem("token") || "";

      // No llmId means a Python agent — its transfer numbers live only in
      // AgentTransferNumbers, which is written further down. Refusing to save
      // here left those agents unable to configure transfers at all.
      if (!agentId) {
        setShowPopup(true);
        setPopupType("failed");
        setPopupMessage("Agent not found.");
        return;
      }

      if (!transfers.length) {
        setShowPopup(true);
        setPopupType("failed");
        setPopupMessage("Please add at least one department number.");
        return;
      }

      // 1) Validate
      //    Department name errors appear under the corresponding field
      //    (via showDepartmentErrors) instead of a popup, so the user
      //    sees exactly which row is wrong.
      for (const [i, t] of transfers.entries()) {
        const finalCond =
          t._mode === "custom"
            ? normalizeCondition(t._customName)
            : normalizeCondition(t.condition);

        const departmentError = getDepartmentNameError(finalCond);

        if (departmentError) {
          setShowDepartmentErrors(true);
          return;
        }

        const phone = (t.phone || "").trim();
        const dialCode = (t.dialCode || "").trim();

        if (!phone || !dialCode) {
          setShowPopup(true);
          setPopupType("failed");
          setPopupMessage(
            `Phone number and dial code are required for entry ${i + 1}.`
          );
          return;
        }

        const parsed = parsePhoneNumberFromString(`+${phone}`);
        if (!parsed) {
          setShowPopup(true);
          setPopupType("failed");
          setPopupMessage(`Invalid phone number for entry ${i + 1}.`);
          return;
        }

        const email = (t.email || "").trim();

        if (email && email !== "null") {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(email)) {
            setShowPopup(true);
            setPopupType("failed");
            setPopupMessage(`Invalid email format for entry ${i + 1}.`);
            return;
          }
        }
      }

      setLoading(true);

      // 2) Finalize conditions
      const finalized = transfers.map((t, i) => ({
        ...t,
        condition:
          t._mode === "custom"
            ? normalizeCondition(t._customName)
            : normalizeCondition(t.condition),
        is_fallback: activeToggleIndex === i,
      }));

      const formattedTransfers = prepareTransfersWithDialCode(
        finalized.map(({ _mode, _customName, ...rest }) => rest)
      );

      // 3) Build dynamic vars
      const dynamicVars = {};
      formattedTransfers.forEach((t) => {
        if (!t.condition) return;
        const key = keyFromCondition(t.condition);
        dynamicVars[key] = `+${String(t.phone || "").replace(/[^\d]/g, "")}`;
      });

      // 4) Prompt — use shared builder
      const fullPrompt = buildTransferPrompt(formattedTransfers);
      const timestamp = Date.now();
      const transferTool = {
        type: "transfer_call",
        name: `transfer_on_inferred_${timestamp}`,
        transfer_destination: { type: "inferred", prompt: fullPrompt },
        transfer_option: {
          type: "warm_transfer",
          enable_human_detection: true,
          auto_greet: true,
          voicemail_detection_timeout_ms: 60000,
          transfer_ring_duration_ms: 60000,
          enable_bridge_audio_cue: true,
        },
        custom_sip_headers: {
          "X-Telnyx-Username": `${process.env.REACT_APP_TELNYX_USERNAME}`
        },
        speak_during_execution: true,
        speak_after_execution: true,
        failure_message:
          "I apologize, but the line is busy right now. Please try again later or I can assist you with something else.",
      };

      // 5) Fetch full config — Retell agents only
      let currentConfig = null;
      if (llmId) {
        const llmRes = await fetchLlmDetails(llmId);
        currentConfig = llmRes?.data?.data || llmRes?.data?.data?.data;
        if (!currentConfig) throw new Error("Could not load current LLM config.");
      }

      const existingTools = currentConfig?.general_tools || [];
      const existingVars = currentConfig?.default_dynamic_variables || {};

      // 6) Remove old *_number vars
      const cleanedPrev = { ...existingVars };
      Object.keys(cleanedPrev).forEach((k) => {
        if (/_number$/.test(k)) delete cleanedPrev[k];
      });

      const mergedVars = { ...cleanedPrev, ...dynamicVars };

      // 7) Replace transfer tool
      const toolsWithoutOldTransfer = existingTools.filter(
        (t) =>
          !(
            t?.type === "transfer_call" &&
            String(t?.name || "").startsWith("transfer_on_inferred_")
          )
      );
      const mergedTools = [...toolsWithoutOldTransfer, transferTool];

      // 8) Update LLM
      const payload = {
        ...currentConfig,
        general_tools: mergedTools,
        default_dynamic_variables: mergedVars,
      };

      if (llmId) {
        await updateLlm(token, llmId, payload);
      }

      // 9) Session cache
      syncSessionCache(formattedTransfers);
      setPrevDynamicVar(mergedVars);

      // ✅ 10) Save to DB (bulk sync) including send_sms
      if (agentId) {
        const dbPayload = toDbNumbersPayload(formattedTransfers);
        await bulkSaveAgentTransferNumbers(agentId, dbPayload);
        setSavedRows(formattedTransfers);
        /*
         * Setup Score: triageRulesSaved (10 pts).
         *
         * Fires here so the score advances regardless of how the user
         * reached this screen (wizard wrappers like TriageStep /
         * CallRoutingStep ALSO fire it on their wizardNext, but the
         * standalone /call-transfer route — used by SetupScoreCard's
         * Continue Setup CTA — has no wrapper. Without this mark, that
         * path saved rules to the DB but left triageRulesSaved=false
         * forever in the score. The backend endpoint is idempotent so
         * the wrapper re-fire is harmless.
         */
        try {
          markSetupStep("triageRulesSaved", agentId);
        } catch (_e) {
          /* never let score plumbing fail a save */
        }

        // refresh ids
        try {
          const dbRes = await getAgentTransferNumbers(agentId);
          const rows = dbRes?.data?.data || dbRes?.data || dbRes || [];
          const dbIncoming = Array.isArray(rows)
            ? rows.map(dbRowToTransfer)
            : [];

          const mergedBack = mergeTransfersPreferDB(
            formattedTransfers,
            dbIncoming
          ).map((r) => ({
            ...r,
            _mode: inferModeFromCondition(r.condition),
            _customName:
              inferModeFromCondition(r.condition) === "custom"
                ? r.condition
                : "",
          }));
          setTransfers(mergedBack);
          setSavedRows(mergedBack.map((r) => ({ ...r })));
          const fbIdx = mergedBack.findIndex((t) => t.is_fallback);
          setActiveToggleIndex(fbIdx !== -1 ? fbIdx : null);
        } catch { }
      }

      // Clear any lingering department-error flags now that we've saved.
      setShowDepartmentErrors(false);

      if (wizardMode && typeof onWizardNext === "function") {
        /*
         * Race-condition fix — in wizardMode the parent (e.g.
         * GoLiveWizard's TriageStep) takes over with its own
         * confirmation surface (SophiaReadyDialog). Showing the
         * generic "Numbers updated successfully." popup here meant
         * the user saw BOTH overlapping for ~900ms — the popup
         * sitting on top of the dialog with the avatar peeking
         * behind. Skip the popup in wizardMode and advance
         * immediately; the dialog already confirms success.
         */
        // Pass the saved rule count so wizard wrappers can report it to GA4.
        onWizardNext(formattedTransfers.length);
      } else {
        backOnCloseRef.current = Boolean(returnTo);
        setShowPopup(true);
        setPopupType("success");
        setPopupMessage("Numbers updated successfully.");
      }
    } catch (error) {
      console.error("Failed to update LLM:", error);
      setShowPopup(true);
      setPopupType("failed");
      setPopupMessage(
        "Failed to update LLM: " +
        (error?.response?.data || error?.message || error)
      );
    } finally {
      setLoading(false);
    }
  };

  const initializing = !bizLoaded || !llmLoaded;

  return (
    <>
      {initializing && (
        <div className={styles.loaderOverlay}>
          <Loader2 />
        </div>
      )}
      {/* add */}
      <div
        className={`${styles.CallTransferMainDiv}${initializing ? ` ${styles.dimmed}` : ""}`}
      >
        {!wizardMode && (
          // Standalone page: header scroll par upar chipka rahe.
          <div className={styles.stickyHeader}>
            <HeaderBar title="Dynamic Call Transfer" />
          </div>
        )}
        <div className={styles.CallTransferMain}>
          {customHeader ? customHeader : (
            /*
              Spec headings (Figma 13 Step 3) — only rendered when the parent
              opts in by passing the `headings` prop. PostPurchaseSetup and the
              standalone /call-transfer route omit it and keep the existing
              short "Call Triage" h2 below. GoLiveWizard passes spec copy.
            */
            headings && (headings.lightTitle || headings.boldTitle || headings.subtitle) && (
              <div className={styles.headingsBlock}>
                {headings.lightTitle && (
                  <h4 className={styles.headingLight}>
                    {headings.lightTitle}
                  </h4>
                )}
                {headings.boldTitle && (
                  <h5 className={styles.headingBold}>
                    {headings.boldTitle}
                  </h5>
                )}
                {headings.subtitle && (
                  <p className={styles.headingSub}>
                    {headings.subtitle}
                  </p>
                )}
              </div>
            ))}
          <div className={styles.headrPart}>
            <h2>Call Triage</h2>

            <img
              src="svg/Add-icon.svg"
              alt="Add-icon"
              onClick={handleAdd}
              className={`${styles.addIcon}${transfers.some(
                (t) => !(t.condition || "").trim() && !(t.phone || "").trim()
              )
                ? ` ${styles.addIconDisabled}`
                : ""
                }`}
            />
          </div>

          {transfers.map((item, index) => {
            const selectedDepartments = getSelectedDepartments(
              transfers.filter((_, i) => i !== index)
            );
            const mode = item._mode || inferModeFromCondition(item.condition);
            const selectedPreset = PRESETS.find(
              (p) => p.toLowerCase() === (item.condition || "").toLowerCase()
            );

            // The value the user actually cares about — preset selection
            // or typed custom name.
            const departmentValue =
              mode === "custom" ? item._customName : item.condition;

            // Only surface errors after the user has attempted Submit.
            // Keeps the field quiet while typing the first time.
            const departmentError = showDepartmentErrors
              ? getDepartmentNameError(departmentValue)
              : "";

            return (
              <div
                key={`${item.condition || "row"}-${index}`}
                className={styles.card}
              >
                <div className={styles.selectWrapper}>
                  <div className={styles.toplabelRow}>
                    <label className={styles.label}>Department</label>
                    {transfers.length > 0 && (
                      <button
                        type="button"
                        onClick={() => handleRemove(index)}
                        className={styles.removeBtn}
                        disabled={removingIndex === index}
                        title="Remove Department"
                      >
                        X
                      </button>
                    )}
                  </div>

                  <select
                    className={`${styles.select}${departmentError ? ` ${styles.departmentInputError}` : ""
                      }`}
                    aria-invalid={Boolean(departmentError)}
                    aria-describedby={
                      departmentError ? `department-error-${index}` : undefined
                    }
                    value={mode === "custom" ? "Custom" : selectedPreset || ""}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "Custom") {
                        updateTransfer(index, {
                          _mode: "custom",
                          _customName:
                            item._customName ||
                            (selectedPreset ? "" : item.condition),
                          condition: "",
                        });
                      } else {
                        updateTransfer(index, {
                          _mode: "preset",
                          condition: val,
                          _customName: "",
                        });
                      }
                    }}
                  >
                    <option value="">Select Department</option>
                    {PRESETS
                      .filter(
                        (p) =>
                          !selectedDepartments.includes(p.toLowerCase())
                      )
                      .map((p) => (
                        <option key={p} value={p}>
                          {p}
                        </option>
                      ))}

                    <option value="Custom">Custom</option>
                  </select>

                  {mode === "custom" && (
                    <input
                      className={`${styles.select} ${styles.customNameInput}${departmentError ? ` ${styles.departmentInputError}` : ""
                        }`}
                      placeholder="Sales/ Quote request—customer wants"
                      value={item._customName || ""}
                      aria-invalid={Boolean(departmentError)}
                      aria-describedby={
                        departmentError ? `department-error-${index}` : undefined
                      }
                      onChange={(e) => {
                        // Silently strip disallowed characters, then store cleaned value.
                        const cleaned = e.target.value.replace(
                          DEPARTMENT_NAME_DISALLOWED,
                          ""
                        );
                        updateTransfer(index, { _customName: cleaned });
                      }}
                    />
                  )}

                  {departmentError && (
                    <p
                      id={`department-error-${index}`}
                      className={styles.departmentFieldError}
                      role="alert"
                    >
                      {departmentError}
                    </p>
                  )}
                </div>

                {/* Header row: "Forward to" label on the left, fallback
                    capsule + label + info icon pinned to the right. The
                    phone input below now spans full width since the
                    toggle has moved up. Same exclusive-fallback +
                    activeToggleIndex behavior as before. */}
                <div className={styles.forwardRow}>
                  <label className={`${styles.label} ${styles.labelInline}`}>
                    Transfer to
                  </label>
                  <div className={styles.fallbackControls}>
                    <Toggle
                      checked={activeToggleIndex === index}
                      // Always enabled — clicking an inactive row's
                      // toggle now auto-switches fallback to it (and
                      // clears the previously active row in one go).
                      // No more "first turn off the other one" dance.
                      onChange={(val) => {
                        if (val) {
                          // Activate THIS row as the fallback, clear
                          // the flag on every other row in a single
                          // setTransfers so React batches the update.
                          setActiveToggleIndex(index);
                          setTransfers((prev) =>
                            prev.map((t, i) => ({
                              ...t,
                              is_fallback: i === index,
                            })),
                          );
                        } else {
                          // User explicitly turned this row off — leave
                          // wizard with no fallback selected.
                          setActiveToggleIndex(null);
                          setTransfers((prev) =>
                            prev.map((t) => ({ ...t, is_fallback: false })),
                          );
                        }
                      }}
                    />
                    <span
                      className={`${styles.makeDefaultLabel}${activeToggleIndex === index ? ` ${styles.makeDefaultLabelActive}` : ""}`}
                    >
                      Make it default
                    </span>
                    <Tooltip
                      content={
                        "When the caller gets frustrated, asks for a real person, or the agent can't resolve the issue, the call is automatically routed to this fallback number. Only one number can be marked as fallback at a time."
                      }
                    />
                  </div>
                </div>

                {(() => {
                  const { dial, national } = splitWizardPhone(item);
                  return (
                    <div
                      className={`${styles.phoneInput} ${styles.phoneInputFull} ${styles.phoneInputWizard}`}
                      // ::before isi se "+1" + divider line banata hai.
                      data-dial={`+${dial}`}
                      style={{ "--dial-chars": dial.length + 1 }}
                    >
                      <PhoneInput
                        // Wahi library + wahi country dropdown. Sirf dial code
                        // input ke andar nahi (disableCountryCode) — isliye
                        // input khaali rehta hai aur placeholder dikhta hai.
                        key={`wiz:${item._syncFingerprint || `init:${item.countryCode || "us"}`}`}
                        country={item.countryCode || "us"}
                        enableSearch
                        disableCountryCode
                        // Country sirf flag dropdown se badalti hai.
                        disableCountryGuess
                        inputProps={{
                          value: national,
                          placeholder: "985 XXX 88XX",
                          onChange: (e) => {
                            const digits = e.target.value.replace(/\D/g, "");
                            updateTransfer(index, {
                              phone: digits ? `${dial}${digits}` : "",
                              dialCode: dial,
                              countryCode: item.countryCode || "us",
                            });
                          },
                        }}
                        onChange={(val, c) => {
                          // This only fires when user selects a country from the flag dropdown
                          const newDial = String(c?.dialCode || dial).replace(/\D/g, "");
                          const newCountry = String(c?.countryCode || "us").toLowerCase();
                          updateTransfer(index, {
                            dialCode: newDial,
                            countryCode: newCountry,
                            phone: national ? `${newDial}${national}` : "",
                          });
                        }}
                        inputClass={styles.phoneNumberInput}
                        dropdownClass={styles.phoneDropdown}
                        inputStyle={{
                          border: "unset",
                          boxShadow: "none",
                          outline: "none",
                          width: "100%",
                        }}
                        dropdownStyle={{ border: "unset", boxShadow: "none" }}
                      />
                    </div>
                  );
                })()}

                <label className={`${styles.label} ${styles.labelSpaced}`}>
                  Email
                </label>

                <input
                  type="text"
                  className={styles.select}
                  placeholder="Enter email (e.g. support@company.com)"
                  value={item.email && item.email !== "null" ? item.email : ""}
                  onChange={(e) =>
                    updateTransfer(index, { email: e.target.value })
                  }
                />
              </div>
            );
          })}

          {wizardMode ? (
            /*
             * Full-width sticky shelf at the bottom of the viewport.
             * Earlier this was a thin centered wrapper at bottom: 64px
             * with no background — body content (the second triage
             * rule card, Email field, etc.) scrolled visibly UNDER
             * the button which made the page look broken on mobile.
             *
             * New layout:
             *   - bottom: 0, full-width with top shadow
             *   - white background opaquely hides scroll content
             *     behind the shelf
             *   - paddingBottom 56px reserves space for the Skip link
             *     that the wizard's TriageStep renders right under
             *     the button (also fixed, but inside this shelf's
             *     visual region)
             */
            <>
              <div style={{ height: shelfHeight }} aria-hidden="true" />
              <div className={styles.wizardShelf} ref={wizardShelfRef}>
                <div className={styles.wizardShelfInner}>
                  <AnimatedButton
                    isLoading={loading}
                    label={loading ? "Saving..." : "Save & Continue"}
                    onClick={handleSubmit}
                    positionType="relative"
                    disabled={savingSmsToggle || loading}
                  />
                </div>
              </div>
            </>
          ) : (
            <div
              className={`${styles.Btn}${!isDirty ? ` ${styles.BtnDisabled}` : ""}`}
              aria-disabled={!isDirty}
            >
              <AnimatedButton
                isLoading={loading}
                label="Submit"
                onClick={handleSubmit}
                positionType="relative"
                bottom="0px"
                disabled={loading || !isDirty}
              />
            </div>
          )}
        </div>
      </div>

      {showPopup && (
        <PopUp
          type={popupType}
          onClose={() => {
            setShowPopup(false);
            // -1: Settings pe nayi history entry na bane (back loop)
            if (backOnCloseRef.current) {
              backOnCloseRef.current = false;
              navigate(-1);
            }
          }}
          message={popupMessage}
        />
      )}

      {deleting && (
        <div className={styles.deletingOverlay}>
          <Loader2 />
        </div>
      )}
    </>
  );
}

export default CallTransfer;